// Dashboard preset configs — additive CSS Grid layout above the
// existing component CSS. Same components per preset; only the grid
// area assignment and visibility differ. No layout library.
//
// Preset shape:
//   panels: { panelName: { visible, gridArea, size?, mode? } }
//   gridTemplateAreas / Columns / Rows: CSS Grid container values.
//
// `mode: 'on-demand'` means the panel is not rendered in the main grid;
// it's mounted into a slide-up sheet, opened by a button.
//
// `externalAdvisor: true` means the AI Advisor is rendered as a
// standalone panel; Dashboard.css uses this to hide the AI Advisor
// instance nested inside GamePlayer2d to avoid a double render.
//
// `externalRiver: true` is the same pattern for TheRiver. When true,
// Dashboard.css hides the TheRiver instance nested inside GamePlayer2d
// so only the standalone orbitalRiver panel renders. Set false on
// presets where the River is suppressed in the standalone panel and
// the nested copy carries the player's view of the orbits.

export const PHONE_COMPANION = 'phone_companion';
export const TV_OBSERVER = 'tv_observer';
export const TABLET = 'tablet';
export const DESKTOP = 'desktop';

export const PRESET_NAMES = [
    PHONE_COMPANION, TV_OBSERVER, TABLET, DESKTOP,
];

export const PRESET_DISPLAY_NAMES = {
    [PHONE_COMPANION]: 'Phone Companion',
    [TV_OBSERVER]: 'TV Observer',
    [TABLET]: 'Tablet',
    [DESKTOP]: 'Desktop',
};

export const PRESETS = {
    [PHONE_COMPANION]: {
        displayName: 'Phone Companion',
        externalAdvisor: true,
        externalRiver: false,
        panels: {
            topBar:        { visible: true,  gridArea: 'top' },
            yourHotel:     { visible: true,  gridArea: 'hotel' },
            ticker:        { visible: true,  gridArea: 'ticker' },
            orbitalRiver:  { visible: false, gridArea: null },
            competitors:   { visible: true,  gridArea: 'competitors' },
            aiAdvisor:     { visible: true,  gridArea: 'advisor' },
            // Deck and pools surfaces are reachable via the existing
            // in-game affordances inside GamePlayer2d (DeckBar /
            // Decks2d / Pools toggle) — those still work and are
            // unaffected by the dashboard wrapper. Exposing the
            // dashboard's on-demand buttons here would open empty
            // sheets, so they stay defined but invisible until a
            // future CC wires them properly.
            poolsPanel:    { visible: false, gridArea: null },
            deckInspector: { visible: false, gridArea: null },
        },
        // Reading order top-to-bottom: top bar → AI advisor →
        // own hotel (the star) → competitors slim band → ticker.
        // Advisor pinned directly under the top bar so the
        // captain's proposed move is the first thing the player
        // reads on the phone.
        //
        // Competitors row is `auto`: HotelObserver renders at its
        // natural ResizeObserver-driven size and the row sizes to
        // fit. The earlier `120px` value assumed a thumbnail clip
        // rule that turned out to collapse the board to ~0 — that
        // rule is gone, this row sizing follows.
        gridTemplateAreas: `
            "top         top"
            "advisor     advisor"
            "hotel       hotel"
            "competitors competitors"
            "ticker      ticker"
        `,
        gridTemplateColumns: '1fr 1fr',
        gridTemplateRows: 'auto auto 1fr auto auto',
    },

    [TV_OBSERVER]: {
        displayName: 'TV Observer',
        externalAdvisor: false,
        externalRiver: true,
        panels: {
            topBar:        { visible: true,  gridArea: 'top' },
            yourHotel:     { visible: false, gridArea: null },
            ticker:        { visible: true,  gridArea: 'ticker' },
            orbitalRiver:  { visible: true,  gridArea: 'river',
                             size: 'prominent' },
            competitors:   { visible: true,  gridArea: 'competitors',
                             size: 'grid-6' },
            aiAdvisor:     { visible: false, gridArea: null },
            poolsPanel:    { visible: true,  gridArea: 'pools' },
            deckInspector: { visible: true,  gridArea: null,
                             mode: 'on-demand' },
        },
        gridTemplateAreas: `
            "top         top         top"
            "river       river       river"
            "competitors competitors pools"
            "ticker      ticker      ticker"
        `,
        gridTemplateColumns: '1fr 1fr 320px',
        gridTemplateRows: 'auto 180px 1fr auto',
    },

    [TABLET]: {
        displayName: 'Tablet',
        externalAdvisor: true,
        externalRiver: true,
        panels: {
            topBar:        { visible: true,  gridArea: 'top' },
            yourHotel:     { visible: true,  gridArea: 'hotel' },
            ticker:        { visible: true,  gridArea: 'ticker' },
            orbitalRiver:  { visible: true,  gridArea: 'river' },
            competitors:   { visible: true,  gridArea: 'competitors',
                             size: 'band' },
            aiAdvisor:     { visible: true,  gridArea: 'advisor' },
            poolsPanel:    { visible: true,  gridArea: null,
                             mode: 'on-demand' },
            deckInspector: { visible: true,  gridArea: null,
                             mode: 'on-demand' },
        },
        gridTemplateAreas: `
            "top         top         top"
            "river       river       river"
            "hotel       hotel       advisor"
            "hotel       hotel       advisor"
            "competitors competitors competitors"
            "ticker      ticker      ticker"
        `,
        gridTemplateColumns: '1fr 1fr 280px',
        gridTemplateRows: 'auto 140px 1fr 1fr 160px auto',
    },

    [DESKTOP]: {
        displayName: 'Desktop',
        // Desktop uses a bifurcated render path in Dashboard.jsx
        // (see Dashboard.jsx ~line 110-onward). It does NOT consume
        // gridTemplateAreas / gridTemplateColumns / gridTemplateRows
        // — those fields are intentionally absent. The Desktop branch
        // renders top + ticker as full-width bands and a `position:
        // relative` canvas between them inside which three floating
        // windows (advisor, hotel, competitors) live as draggable,
        // resizable, z-orderable react-rnd children. Position, size,
        // and z-index persist per panel to localStorage via
        // useFloatingWindows() in src/components/Dashboard/.
        //
        // Visibility flags below match Phone Companion's exactly —
        // same five visible panels, same three hidden ones. Pools
        // and deck remain reachable via the existing in-game score-
        // bar tap inside GamePlayer2d.
        //
        // externalAdvisor / externalRiver are deliberately omitted.
        // Phone Companion's `dashboard--external-advisor` root-class
        // CSS rule must not apply here — the Desktop branch has its
        // own scoped rule for hiding the nested advisor copy (see
        // Dashboard.css). This is the WET split that keeps Phone
        // Companion bit-for-bit untouched.
        panels: {
            topBar:        { visible: true,  gridArea: null },
            yourHotel:     { visible: true,  gridArea: null },
            ticker:        { visible: true,  gridArea: null },
            orbitalRiver:  { visible: false, gridArea: null },
            competitors:   { visible: true,  gridArea: null },
            aiAdvisor:     { visible: true,  gridArea: null },
            poolsPanel:    { visible: false, gridArea: null },
            deckInspector: { visible: false, gridArea: null },
        },
    },
};

// Paren-aware whitespace tokenizer for grid track lists.
// `gridTemplateRows` values can contain commas and spaces inside
// function calls like `minmax(190px, 1fr)`. A naive `split(/\s+/)`
// would shred those into three pieces. Walk the string, only split
// on whitespace at depth 0.
export function tokenizeGridTracks(input) {
    if (!input) return [];
    const tokens = [];
    let depth = 0;
    let current = '';
    for (let i = 0; i < input.length; i++) {
        const ch = input[i];
        if (ch === '(') {
            depth++;
            current += ch;
        } else if (ch === ')') {
            depth--;
            current += ch;
        } else if (/\s/.test(ch) && depth === 0) {
            if (current) {
                tokens.push(current);
                current = '';
            }
        } else {
            current += ch;
        }
    }
    if (current) tokens.push(current);
    return tokens;
}

// Returns a preset with every full-row use of `areaName` collapsed
// out of the CSS Grid template. Used when a panel becomes empty at
// runtime (e.g., orbital river with no guests in any orbit) and the
// row should occupy zero real estate, not leave a reserved track.
//
// Rules of operation:
//   1. Find the panel whose `gridArea` matches `areaName` and is
//      currently visible. If none, the preset is unchanged.
//   2. From `gridTemplateAreas`, drop every row whose tokens are
//      all `areaName` (rows that mix `areaName` with other areas
//      are left alone — partial collapse is out of scope).
//   3. From `gridTemplateRows`, drop the entries at the same
//      indices as the rows dropped in step 2.
//   4. Flip the panel's `visible` to false and `gridArea` to null
//      so the <Panel> wrapper returns null on render.
//   5. Drop `externalRiver` / `externalAdvisor` correspondence if
//      the collapsed panel was the external one, so the
//      `dashboard--external-*` root class no longer claims an
//      external copy that no longer exists.
export function collapseEmptyAreaFromPreset(preset, areaName) {
    if (!preset || !preset.panels || !preset.gridTemplateAreas) {
        return preset;
    }
    let panelKey = null;
    for (const key of Object.keys(preset.panels)) {
        const cfg = preset.panels[key];
        if (cfg && cfg.gridArea === areaName && cfg.visible) {
            panelKey = key;
            break;
        }
    }
    if (!panelKey) return preset;

    const rawRows = preset.gridTemplateAreas
        .split('\n').map((s) => s.trim()).filter(Boolean);
    const dropIndices = new Set();
    rawRows.forEach((row, i) => {
        const stripped = row.replace(/^"/, '').replace(/"$/, '').trim();
        if (!stripped) return;
        const tokens = stripped.split(/\s+/);
        if (tokens.length > 0
            && tokens.every((t) => t === areaName)) {
            dropIndices.add(i);
        }
    });
    if (dropIndices.size === 0) return preset;

    const keptRows = rawRows.filter((_, i) => !dropIndices.has(i));
    const rowSizes = tokenizeGridTracks(preset.gridTemplateRows);
    const keptRowSizes = rowSizes.filter(
        (_, i) => !dropIndices.has(i));

    const nextPanel = {
        ...preset.panels[panelKey],
        visible: false,
        gridArea: null,
    };

    const collapsed = {
        ...preset,
        gridTemplateAreas: keptRows.join('\n'),
        gridTemplateRows: keptRowSizes.join(' '),
        panels: {
            ...preset.panels,
            [panelKey]: nextPanel,
        },
    };
    if (areaName === 'river' && collapsed.externalRiver) {
        collapsed.externalRiver = false;
    }
    if (areaName === 'advisor' && collapsed.externalAdvisor) {
        collapsed.externalAdvisor = false;
    }
    return collapsed;
}
